import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const LiveTicker = () => {
  const [matches, setMatches] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);
  const wsRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const autoScrollRef = useRef(null);
  const reconnectRef = useRef(null);

  const fetchTickerData = useCallback(async () => {
    try {
      const response = await fetch('/api/ticker/live');
      if (response.ok) {
        const data = await response.json();
        setMatches(data.matches || []);
      }
    } catch (e) {
      console.error('Ticker fetch error:', e);
    }
  }, []);

  // WebSocket connection
  const connectWS = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    try {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${window.location.host}/api/ws/ticker`);
      wsRef.current = ws;
      ws.onopen = () => {
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.matches) setMatches(data.matches);
        } catch {}
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (!pollIntervalRef.current) {
          pollIntervalRef.current = setInterval(fetchTickerData, 15000);
          fetchTickerData();
        }
        reconnectRef.current = setTimeout(connectWS, 5000);
      };
      ws.onerror = () => ws.close();
      const ping = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 25000);
      ws._ping = ping;
    } catch {
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(fetchTickerData, 15000);
        fetchTickerData();
      }
    }
  }, [fetchTickerData]);

  useEffect(() => {
    fetchTickerData();
    connectWS();
    return () => {
      if (wsRef.current) { clearInterval(wsRef.current._ping); wsRef.current.close(); }
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [connectWS, fetchTickerData]);

  // Auto-scroll
  useEffect(() => {
    autoScrollRef.current = setInterval(() => {
      if (!tickerRef.current || isPaused) return;
      const el = tickerRef.current;
      const max = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= max - 10) el.scrollTo({ left: 0, behavior: 'smooth' });
      else el.scrollTo({ left: el.scrollLeft + 220, behavior: 'smooth' });
    }, 4000);
    return () => clearInterval(autoScrollRef.current);
  }, [isPaused]);

  const scroll = (dir) => {
    if (!tickerRef.current) return;
    const amt = 300;
    tickerRef.current.scrollBy({ left: dir === 'left' ? -amt : amt, behavior: 'smooth' });
  };

  if (matches.length === 0) return null;

  return (
    <div
      className="fixed top-16 left-0 right-0 z-40 bg-[#0d0d0d] border-b border-white/[0.06]"
      data-testid="live-ticker"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-[1400px] mx-auto px-1 relative">
        {/* Left fade + arrow */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-r from-[#0d0d0d] via-[#0d0d0d]/90 to-transparent pl-1 pr-5 text-white/40 hover:text-white transition-colors"
          aria-label="Scroll left"
          data-testid="ticker-scroll-left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Ticker strip */}
        <div
          ref={tickerRef}
          className="flex items-stretch overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {matches.map((match, idx) => (
            <TickerCard key={match.id || idx} match={match} isLast={idx === matches.length - 1} />
          ))}
        </div>

        {/* Right fade + arrow */}
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-l from-[#0d0d0d] via-[#0d0d0d]/90 to-transparent pr-1 pl-5 text-white/40 hover:text-white transition-colors"
          aria-label="Scroll right"
          data-testid="ticker-scroll-right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

function TickerCard({ match, isLast }) {
  const isLive = match.status === 'live';
  const isHT = match.status === 'ht';
  const isFT = match.status === 'ft';
  const isActive = isLive || isHT || isFT;

  // Count events by type
  const goals = (match.events || []).filter(e => ['goal', 'penalty', 'own_goal'].includes(e.event_type));
  const redCards = (match.events || []).filter(e => e.event_type === 'red_card');

  return (
    <div className={`flex-shrink-0 flex items-center ${!isLast ? 'border-r border-white/[0.06]' : ''}`}>
      <div className="flex items-center gap-2 px-4 py-2 hover:bg-white/[0.03] transition-colors cursor-default min-w-[260px]">
        {/* Status badge */}
        <div className="w-[42px] shrink-0">
          {isLive ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-[10px] font-black text-white tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              LIVE
            </span>
          ) : isHT ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-600/80 text-[10px] font-black text-white tracking-wide">
              HT
            </span>
          ) : isFT ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-bold text-white/50 tracking-wide">
              FT
            </span>
          ) : (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-600/20 text-[10px] font-bold text-blue-400 tracking-wide">
              SOON
            </span>
          )}
        </div>

        {/* Teams and score */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {/* Home */}
          <span className={`text-[13px] font-semibold truncate max-w-[85px] ${
            isActive && match.home_score > match.away_score ? 'text-white' : 'text-white/70'
          }`}>
            {match.home_team}
          </span>

          {/* Score */}
          {isActive ? (
            <span className="flex items-center gap-1 text-[13px] font-bold tabular-nums shrink-0 px-1">
              <span className={match.home_score > match.away_score ? 'text-emerald-400' : 'text-white'}>
                {match.home_score}
              </span>
              <span className="text-white/25">—</span>
              <span className={match.away_score > match.home_score ? 'text-emerald-400' : 'text-white'}>
                {match.away_score}
              </span>
            </span>
          ) : (
            <span className="text-white/25 text-xs shrink-0 px-1">vs</span>
          )}

          {/* Away */}
          <span className={`text-[13px] font-semibold truncate max-w-[85px] ${
            isActive && match.away_score > match.home_score ? 'text-white' : 'text-white/70'
          }`}>
            {match.away_team}
          </span>
        </div>

        {/* Minute + Events */}
        <div className="flex items-center gap-1 shrink-0 ml-1">
          {/* Event icons */}
          {goals.length > 0 && (
            <span className="text-[11px]" title={`${goals.length} goal(s)`}>⚽</span>
          )}
          {redCards.length > 0 && (
            <span className="text-[11px]" title={`${redCards.length} red card(s)`}>🟥</span>
          )}

          {/* Minute display */}
          {isLive && match.minute && (
            <span className="text-[11px] text-white/40 font-medium tabular-nums">
              {match.minute}
            </span>
          )}
          {isHT && (
            <span className="text-[11px] text-yellow-500/60 font-medium">
              ⏸
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default LiveTicker;
